import type { TourStep } from "../tour-steps";

/** Tours de página do domínio Clínica — cada um roda inteiro numa única rota (nunca navega),
 *  então `route` só existe pra satisfazer o tipo compartilhado com o tour global. */

export const AGENDA_TOUR_STEPS: TourStep[] = [
  {
    id: "agenda-modo-visualizacao",
    route: "/agenda",
    target: '[data-tour="agenda-modo-visualizacao"]',
    title: "Troque a visualização",
    content: "Alterne entre Dia, Semana, Quinzena e Mês pra ver a agenda no formato que fizer mais sentido pro seu fluxo.",
    skipBeacon: true,
  },
  {
    id: "agenda-filtros",
    route: "/agenda",
    target: '[data-tour="agenda-filtros"]',
    title: "Filtre por dentista ou procedimento",
    content: "Esconde da grade quem não te interessa no momento — útil quando vários profissionais dividem a mesma agenda.",
    skipBeacon: true,
  },
  {
    id: "agenda-nova-consulta",
    route: "/agenda",
    target: '[data-tour="agenda-nova-consulta"]',
    title: "Agende em poucos cliques",
    content: "Escolha o paciente e o procedimento — a duração e o horário sugerido já vêm calculados a partir das suas preferências.",
    skipBeacon: true,
    demo: "schedule",
  },
];

export const PACIENTES_TOUR_STEPS: TourStep[] = [
  {
    id: "pacientes-filtro-status",
    route: "/pacientes",
    target: '[data-tour="pacientes-filtro-status"]',
    title: "Pacientes inativos ficam escondidos",
    content: "Por padrão só mostramos os Ativos. Troque pra Inativos ou Todos quando precisar achar alguém que não vem há um tempo.",
    skipBeacon: true,
  },
  {
    id: "pacientes-gerar-link",
    route: "/pacientes",
    target: '[data-tour="pacientes-gerar-link"]',
    title: "Deixe o paciente se cadastrar sozinho",
    content: "Gere um link de auto-cadastro e envie por WhatsApp — o paciente preenche os próprios dados antes da consulta.",
    skipBeacon: true,
  },
  {
    id: "pacientes-novo",
    route: "/pacientes",
    target: '[data-tour="pacientes-novo"]',
    title: "Cadastre pacientes em segundos",
    content: "Nome e telefone já bastam pra começar — o resto da ficha você completa depois, quando precisar.",
    skipBeacon: true,
    demo: "fill-form",
  },
];

/** Rota tem :id na URL — precisa do pathname real do momento (evita disparar navigate() indevido
 *  ao trocar de passo, já que `handleEvent` compara `route` com `location.pathname`). */
export function getPatientDetailTourSteps(pathname: string): TourStep[] {
  return [
    {
      id: "paciente-detalhe-abas",
      route: pathname,
      target: '[data-tour="paciente-detalhe-abas"]',
      title: "Tudo do paciente num só lugar",
      content: "Início, Dados, Consultas e Histórico (e mais abas, conforme seu plano) ficam organizados aqui.",
      skipBeacon: true,
    },
    {
      id: "paciente-detalhe-agendar",
      route: pathname,
      target: '[data-tour="paciente-detalhe-agendar"]',
      title: "Agende sem sair da ficha",
      content: "Marca uma consulta pra esse paciente direto daqui, sem precisar ir até a Agenda.",
      skipBeacon: true,
      demo: "schedule",
    },
  ];
}

export const PROTESE_TOUR_STEPS: TourStep[] = [
  {
    id: "protese-tabs",
    route: "/protese",
    target: '[data-tour="protese-tabs"]',
    title: "Pedidos, parceiros e outras organizações",
    content: "Além dos seus pedidos, acompanhe os laboratórios parceiros e — se você tiver mais de uma organização — os pedidos feitos por elas.",
    skipBeacon: true,
  },
  {
    id: "protese-status-filtro",
    route: "/protese",
    target: '[data-tour="protese-status-filtro"]',
    title: "Filtre por status",
    content: "Aguardando, Aceito, Em produção, Entregue... cada chip mostra quantos pedidos estão naquele estágio.",
    skipBeacon: true,
  },
  {
    id: "protese-visualizacao",
    route: "/protese",
    target: '[data-tour="protese-visualizacao"]',
    title: "Cards ou lista, você escolhe",
    content: "Cards são melhores pra visualizar rápido; Lista ajuda quando você tem muitos pedidos e quer comparar informações lado a lado.",
    skipBeacon: true,
  },
  {
    id: "protese-novo-pedido",
    route: "/protese",
    target: '[data-tour="protese-novo-pedido"]',
    title: "Peça uma prótese em poucos cliques",
    content: "Escolha o laboratório parceiro, o paciente e o serviço. O pedido cai direto na fila de produção do laboratório.",
    skipBeacon: true,
    demo: "send-flow",
  },
];

export const SERVICOS_TOUR_STEPS: TourStep[] = [
  {
    id: "servicos-filtro-categoria",
    route: "/procedimentos",
    target: '[data-tour="servicos-filtro-categoria"]',
    title: "Organizados por especialidade",
    content: "Cada especialidade tem o seu cartão com os procedimentos dela. Clique no preço pra editar direto na linha; \"Definir preço\" marca o que ainda está sem valor.",
    skipBeacon: true,
  },
  {
    id: "servicos-busca",
    route: "/procedimentos",
    target: '[data-tour="servicos-busca"]',
    title: "Busque por nome",
    content: "Digite parte do nome do procedimento pra encontrar rápido, sem precisar rolar a lista.",
    skipBeacon: true,
  },
  {
    id: "servicos-novo",
    route: "/procedimentos",
    target: '[data-tour="servicos-novo"]',
    title: "Adicione procedimentos",
    content: "Escolha da base da plataforma, sugeridos pelas suas especialidades. Não achou? Crie o seu ali mesmo — ele já fica disponível pra consultas e planos.",
    skipBeacon: true,
    demo: "fill-form",
  },
];

/** Mesmo componente serve /arquivos e /lab/arquivos — precisa do pathname real do momento pelo
 *  mesmo motivo do paciente-detalhe (evita navigate() indevido entre passos). */
export function getArquivosTourSteps(pathname: string): TourStep[] {
  return [
    {
      id: "arquivos-busca-global",
      route: pathname,
      target: '[data-tour="arquivos-busca-global"]',
      title: "Busque em todas as pastas de uma vez",
      content: "Em vez de navegar pasta por pasta, use a busca global pra achar um arquivo em qualquer lugar da organização.",
      skipBeacon: true,
    },
    {
      id: "arquivos-visualizacao",
      route: pathname,
      target: '[data-tour="arquivos-visualizacao"]',
      title: "Grade ou lista",
      content: "Grade é melhor pra imagens e modelos 3D; lista ajuda quando você quer ver nome, tamanho e data de uma vez.",
      skipBeacon: true,
    },
    {
      id: "arquivos-nova-pasta",
      route: pathname,
      target: '[data-tour="arquivos-nova-pasta"]',
      title: "Organize em pastas",
      content: "Crie pastas pra separar arquivos por assunto — os atalhos de Pacientes, Parceiros, Pedidos e Serviços já vêm prontos.",
      skipBeacon: true,
      demo: "list-add",
    },
  ];
}
